import React, {useEffect, useRef, useState} from 'react';
import Breakpoint from '@core/scripts/helper/breakpoint.js';
import RadioInput from 'deprecated-shared-ui/templates/input/Radio.jsx';
import InputGroup from 'deprecated-shared-ui/templates/input/Group.jsx';
import MessageBroker from 'deprecated-shared-ui/components/message-broker';
import fetchWithCSRF from '@core/scripts/helper/fetch-with-csrf';
import URL from '@core/scripts/helper/url';
import Logger from '@core/scripts/components/logger';
import WishlistMessages from '@shopping-funnel/scripts/components/messages';
import {IconButton, BottomSheet, Typography, Select} from '@bonprix/design-system';
import Trinity from '@core/scripts/components/trinity-adapter.js';

const MOBILE_DEVICE_BREAKPOINTS = ['start', 'small']; // , 'medium'

/**
 *
 * @param texts
 * @param preselectedOption
 * @param {AppSort} appSortButton
 */
export default function WishlistSorting({texts, preselectedOption, appSortButton}) {
    const availableOptions = [
        'wishlist_sort_last_added',
        'wishlist_sort_low_price',
        // 'wishlist_sort_best_rated',
        'wishlist_sort_category_az',
        'wishlist_sort_category_za',
        'wishlist_sort_sale',
        'wishlist_sort_availability',
    ];

    const SORTING_OPTION_TRINITY_MAP = {
        wishlist_sort_last_added: 'lastAdded',
        wishlist_sort_low_price: 'lowPrice',
        wishlist_sort_best_rated: 'bestRated',
        wishlist_sort_category_az: 'categoryAZ',
        wishlist_sort_category_za: 'categoryZA',
        wishlist_sort_sale: 'sale',
        wishlist_sort_availability: 'availability',
    };

    // tracking refs
    const filterClickRef = useRef(null);
    const currentOptionRef = useRef(preselectedOption);

    const [currentOption, setCurrentOption] = useState(preselectedOption);
    const [bottomSheetState, setBottomSheetState] = useState(false);

    const triggerSorting = (sortingName) =>
        MessageBroker.publish(WishlistMessages.WISHLIST.SORTING_CHANGE, {sortingName: sortingName});

    useEffect(() => {
        triggerSorting(currentOption);
        currentOptionRef.current = currentOption;
    }, [currentOption]);

    const handleClickSelect = (event) => {
        if (!filterClickRef.current) {
            return;
        }

        const open = filterClickRef.current.getElementsByTagName('button').item(0)?.ariaExpanded === 'true';
        if (!open) {
            // we can't track closing the dropdown when done by clicking on it
            // so we can never reliably determine if a click anywhere did actually close it,
            // or it was closed before
            return;
        }
        trackSortingLabelClick(open);
    };

    // Workaround to write a Landmark when the Sorting Select is opened
    useEffect(() => {
        window.addEventListener('click', handleClickSelect);
        return () => {
            window.removeEventListener('click', handleClickSelect);
        };
    }, []);

    const renderOrderingOptions = (option, onChange) => {
        return (
            <div className="sorting__option" key={option}>
                <InputGroup className="sorting__radio-button">
                    <RadioInput
                        name="orderings"
                        value={option}
                        disabled={false}
                        checked={option === currentOption}
                        onChange={() => onChange(option)}
                        key={option}
                    />
                    <div className="input-group__text">{texts[option]}</div>
                </InputGroup>
            </div>
        );
    };

    const handleOnChange = (selectedKey) => {
        trackSortingOptionChange(selectedKey);

        fetchWithCSRF(URL.toAbsolute(`/list/wishlist/set-sort-option/${selectedKey}/`), {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
            }),
        })
            .then((response) => {
                if (response.status >= 400) {
                    Logger.error('Updating the sorting method failed.');
                }
            })
            .catch((error) => {
                Logger.error('Updating the sorting method failed.');
            });

        setCurrentOption(selectedKey);
    };

    const handleOnChangeBottomSheet = (selectedKey) => {
        handleOnChange(selectedKey);
        setBottomSheetState(false);
    };

    const trackSortingLabelClick = (openState) => {
        const attributes = {
            clickName: openState ? 'open' : 'close',
            currentValue: SORTING_OPTION_TRINITY_MAP[currentOptionRef.current] ?? currentOptionRef.current,
            object: 'dropdown',
        }

        const schema = {
            name: 'CLICK_WISHLIST_SORTING_DROPDOWN',
            version: 1
        };

        Trinity.sendTrinityEvent({schema: schema, attributes: attributes});
    };

    const trackSortingOptionChange = (selectedKey) => {
        const attributes = {
            clickName: 'wishlistSortingApply',
            selectedValue: SORTING_OPTION_TRINITY_MAP[selectedKey] ?? selectedKey,
            previousValue: SORTING_OPTION_TRINITY_MAP[currentOption] ?? currentOption,
            object: 'dropdown',
        }

        const schema = {
            name: 'CLICK_WISHLIST_SORTING_APPLY',
            version: 1
        };

        Trinity.sendTrinityEvent({schema: schema, attributes: attributes});
    };

    const onOpenerClick = () => {
        setBottomSheetState(true);
    };

    const onBottomSheetClose = () => {
        setBottomSheetState(false);
    };

    appSortButton.useSortButtonCallback(onOpenerClick);

    return (
        <div className="filter__groups" ref={filterClickRef} data-native-sort={appSortButton.nativeSortButtonActive}>
            <div className="wishlist__filter">
                {Breakpoint.keyIsIn(MOBILE_DEVICE_BREAKPOINTS) || appSortButton.nativeSortButtonActive ? (
                    <>
                        {appSortButton.nativeSortButtonActive ? (
                            <></>
                        ) : (
                            <IconButton onClick={onOpenerClick} variant="secondary" icon="sort" />
                        )}

                        <BottomSheet isOpen={bottomSheetState} onClose={onBottomSheetClose}>
                            <BottomSheet.Content>
                                <div className="bottom-sheet-headline-container">
                                    <Typography variant="bonprix-h5" component="div" color="primary" align="center">
                                        {texts.wishlist_sort_btn}
                                    </Typography>
                                </div>
                                <div className="wishlist-mobile__filter">
                                    <div className="orderings-list">
                                        {availableOptions.map((option) => {
                                            return renderOrderingOptions(option, handleOnChangeBottomSheet);
                                        })}
                                    </div>
                                </div>
                            </BottomSheet.Content>
                        </BottomSheet>
                    </>
                ) : (
                    <div className="filtering">
                        <Select label="Sortieren" value={currentOption} onValueChange={handleOnChange}>
                            {availableOptions.map((option, index) => {
                                return (
                                    <Select.Item key={index} value={option} selected={false} className="filtering">
                                        {texts[option]}
                                    </Select.Item>
                                );
                            })}
                        </Select>
                    </div>
                )}
            </div>
        </div>
    );
}
